import React, { useCallback, useEffect, useState } from "react";
import {
  Award, CheckCircle2, CircleDot, Clock, Eye, Hourglass, MailCheck, RefreshCw, Send, XCircle, Zap,
} from "lucide-react";
import {
  AppButton, AppCard, AppCardContent, AppCardDescription, AppCardHeader, AppCardTitle,
  Callout, StatusBadge, WhatsAppIcon, cn,
} from "../../ui";
import apiClient from "../../../utils/apiClient";

const STATE = {
  on: { icon: Zap, tone: "success", label: "On" },
  scheduled: { icon: Clock, tone: "info", label: "Scheduled" },
  sending: { icon: CircleDot, tone: "live", label: "Going out" },
  done: { icon: CheckCircle2, tone: "success", label: "Done" },
  waiting: { icon: Hourglass, tone: "neutral", label: "Waiting" },
  review: { icon: Eye, tone: "warning", label: "Awaiting your review" },
  cancelled: { icon: XCircle, tone: "neutral", label: "Cancelled" },
};

const ICON_INK = {
  success: "tw-text-success-fg",
  info: "tw-text-info-fg",
  live: "tw-text-danger-fg",
  warning: "tw-text-warning-fg",
  neutral: "tw-text-content-subtle",
};

const TEMPLATE_STATUS = {
  APPROVED: { tone: "success", label: "Approved" },
  PENDING: { tone: "warning", label: "In review" },
  REJECTED: { tone: "danger", label: "Rejected" },
  PAUSED: { tone: "warning", label: "Paused" },
  DISABLED: { tone: "neutral", label: "Disabled" },
  MISSING: { tone: "neutral", label: "Not created" },
};

const fmt = (d) => {
  if (!d) return "";
  try {
    return new Date(d).toLocaleString(undefined, { weekday: "short", day: "numeric", month: "short", hour: "numeric", minute: "2-digit" });
  } catch { return ""; }
};

const sentLine = (sent) => {
  if (!sent || !sent.total) return null;
  const parts = [`${sent.total} ${sent.total === 1 ? "person" : "people"}`];
  if (sent.email) parts.push(`${sent.email} by email`);
  if (sent.whatsapp) parts.push(`${sent.whatsapp} on WhatsApp`);
  if (sent.covered) parts.push(`${sent.covered} already asked with their certificate`);
  if (sent.errors) parts.push(`${sent.errors} failed`);
  return parts.join(" · ");
};

/**
 * AutomationPanel — every message this session sends on its own, when, and to
 * how many so far; and whether WhatsApp can actually deliver them.
 *
 * WhatsApp needs an APPROVED template per message, per institute — Meta's rule,
 * not ours. Until one is approved that message goes by email only, and this is
 * where an admin sees it and fixes it in one click.
 */
const AutomationPanel = ({ apiBase, sessionId, canManage = true, notify, refreshKey = 0 }) => {
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState("");

  const load = useCallback(async () => {
    try {
      const { data: res } = await apiClient.get(`${apiBase}/sessions/${sessionId}/automation`);
      setData(res);
    } catch {
      setData({ error: true });
    }
  }, [apiBase, sessionId]);

  useEffect(() => { load(); }, [load, refreshKey]);

  const templates = async (body, busyKey) => {
    setBusy(busyKey);
    try {
      const { data: res } = await apiClient.post(`${apiBase}/sessions/${sessionId}/whatsapp-templates`, body);
      if (res?.submitted) {
        const ok = res.submitted.filter((t) => t.ok).length;
        const failed = res.submitted.filter((t) => !t.ok);
        notify?.(
          failed.length
            ? `Submitted ${ok}; ${failed.length} refused: ${failed.map((f) => f.error).join(" ")}`
            : `Submitted ${ok} template${ok === 1 ? "" : "s"} to Meta for review.`,
          failed.length ? "error" : "success",
        );
      } else {
        notify?.("Approval status refreshed from WhatsApp.", "success");
      }
      setData((d) => ({ ...d, whatsapp: res.whatsapp }));
    } catch (err) {
      notify?.(err?.response?.data?.message || "WhatsApp could not be reached. Please try again.", "error");
    }
    setBusy("");
  };

  if (!data) return null;
  if (data.error) return <Callout tone="warning">The automation timeline could not be loaded.</Callout>;

  const wa = data.whatsapp || { connected: false, templates: [] };
  const needed = (wa.templates || []).filter((t) => !t.optional);
  const missing = (wa.templates || []).filter((t) => t.status === "MISSING" || t.status === "REJECTED");
  const approved = needed.filter((t) => t.status === "APPROVED").length;

  return (
    <div className="tw-flex tw-flex-col tw-gap-4">
      {data.stillLiveLongAfterEnd && (
        <Callout tone="warning">
          This session is still marked live an hour after its booked end. Nothing that follows it — certificates,
          feedback, “sorry we missed you” — goes out until it is ended.
        </Callout>
      )}

      <AppCard>
        <AppCardHeader>
          <AppCardTitle>What goes out automatically</AppCardTitle>
          <AppCardDescription>Email always; WhatsApp for every message whose template is approved.</AppCardDescription>
        </AppCardHeader>
        <AppCardContent>
          <ol className="tw-m-0 tw-flex tw-list-none tw-flex-col tw-gap-0 tw-p-0">
            {data.steps.map((step, i) => {
              const st = STATE[step.state] || STATE.waiting;
              const Icon = st.icon;
              const sent = sentLine(step.sent);
              return (
                <li key={step.key} className="tw-relative tw-flex tw-gap-3 tw-pb-4 last:tw-pb-0">
                  {i < data.steps.length - 1 && (
                    <span aria-hidden="true" className="tw-absolute tw-bottom-0 tw-left-[11px] tw-top-7 tw-w-px tw-bg-line" />
                  )}
                  <Icon size={24} className={cn("tw-relative tw-shrink-0 tw-bg-surface", ICON_INK[st.tone])} aria-hidden="true" />
                  <div className="tw-min-w-0 tw-flex-1">
                    <div className="tw-flex tw-flex-wrap tw-items-center tw-gap-2">
                      <span className="tw-text-sm tw-font-semibold tw-text-content">{step.label}</span>
                      <StatusBadge status={st.tone}>{st.label}</StatusBadge>
                      {step.at && <span className="tw-text-xs tw-text-content-subtle">{fmt(step.at)}</span>}
                    </div>
                    <p className="tw-m-0 tw-text-xs tw-text-content-subtle">{step.detail}</p>
                    {sent && <p className="tw-m-0 tw-mt-0.5 tw-text-xs tw-text-content-muted"><MailCheck size={12} className="tw-mr-1 tw-inline" aria-hidden="true" />{sent}</p>}
                  </div>
                </li>
              );
            })}
          </ol>
        </AppCardContent>
      </AppCard>

      <AppCard>
        <AppCardHeader className="tw-flex-row tw-items-start tw-justify-between tw-gap-3">
          <div className="tw-min-w-0">
            <AppCardTitle><WhatsAppIcon size={16} className="tw-mr-1.5 tw-inline" aria-hidden="true" />WhatsApp delivery</AppCardTitle>
            <AppCardDescription>
              {wa.connected
                ? `${approved} of ${needed.length} message templates approved. Meta reviews each one, usually within minutes.`
                : "WhatsApp is not connected, so these messages go by email only."}
            </AppCardDescription>
          </div>
          {wa.connected && canManage && (
            <div className="tw-flex tw-shrink-0 tw-flex-wrap tw-gap-2">
              <AppButton size="sm" variant="outline" disabled={!!busy} onClick={() => templates({ sync: true }, "sync")}>
                <RefreshCw size={16} /> {busy === "sync" ? "Checking…" : "Check again"}
              </AppButton>
              {missing.length > 0 && (
                <AppButton size="sm" disabled={!!busy} onClick={() => templates({}, "submit")}>
                  <Send size={16} /> {busy === "submit" ? "Submitting…" : `Submit ${missing.length} for approval`}
                </AppButton>
              )}
            </div>
          )}
        </AppCardHeader>
        {wa.connected ? (
          <AppCardContent className="tw-flex tw-flex-col tw-gap-2">
            {(wa.templates || []).map((t) => {
              const ts = TEMPLATE_STATUS[t.status] || TEMPLATE_STATUS.MISSING;
              return (
                <details key={t.name} className="tw-rounded-md tw-border tw-border-line tw-px-3 tw-py-2">
                  <summary className="tw-flex tw-cursor-pointer tw-flex-wrap tw-items-center tw-gap-2 tw-text-sm">
                    <span className="tw-font-medium tw-text-content">{t.purpose}</span>
                    <StatusBadge status={ts.tone}>{ts.label}</StatusBadge>
                    {t.optional && <span className="tw-text-xs tw-text-content-subtle">optional</span>}
                    <code className="tw-ml-auto tw-text-xs tw-text-content-subtle">{t.name}</code>
                  </summary>
                  <pre className="tw-m-0 tw-mt-2 tw-whitespace-pre-wrap tw-rounded-sm tw-bg-surface-sunken tw-p-2 tw-font-sans tw-text-xs tw-text-content-muted">{t.body}</pre>
                </details>
              );
            })}
            <p className="tw-m-0 tw-text-xs tw-text-content-subtle">
              <Award size={12} className="tw-mr-1 tw-inline" aria-hidden="true" />
              Writing your own instead? Keep these names and the numbered placeholders in this order.
            </p>
          </AppCardContent>
        ) : (
          <AppCardContent>
            <p className="tw-m-0 tw-text-sm tw-text-content-muted">Connect it from WhatsApp → Settings to add WhatsApp to every message above.</p>
          </AppCardContent>
        )}
      </AppCard>
    </div>
  );
};

export default AutomationPanel;
